/**
 * Browser Notification API service.
 * Shows desktop notifications when tab is not focused.
 * No service worker needed — uses simple Notification constructor.
 */

export type PermissionState = NotificationPermission | 'unsupported';

/** Check if Browser Notification API is available and current permission */
export function getPermissionState(): PermissionState {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
}

/** Request notification permission from user */
export async function requestPermission(): Promise<PermissionState> {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  const result = await Notification.requestPermission();
  return result;
}

/** Check if tab is currently focused */
function isTabFocused(): boolean {
  return document.hasFocus() && document.visibilityState === 'visible';
}

interface DesktopNotificationOptions {
  body: string;
  tag?: string;
  data?: { url?: string };
}

/**
 * Show a desktop notification.
 * Only shows if permission granted AND tab is NOT focused.
 * `tag` deduplicates — same tag replaces previous notification.
 */
export function showDesktopNotification(
  title: string,
  options: DesktopNotificationOptions
): void {
  if (getPermissionState() !== 'granted') return;
  if (isTabFocused()) return;

  try {
    const notification = new Notification(title, {
      body: options.body,
      icon: '/favicon.ico',
      tag: options.tag,
      data: options.data,
    });

    notification.onclick = () => {
      window.focus();
      if (options.data?.url) {
        window.location.href = options.data.url;
      }
      notification.close();
    };

    // Auto-close after 8 seconds
    setTimeout(() => notification.close(), 8000);
  } catch {
    // Silently fail — some browsers restrict Notification constructor
  }
}

/** Check if user dismissed the permission banner recently (within 7 days) */
export function isPermissionBannerDismissed(): boolean {
  const dismissed = localStorage.getItem('browser-notif-dismissed');
  if (!dismissed) return false;
  const dismissedAt = parseInt(dismissed, 10);
  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - dismissedAt < sevenDays;
}

/** Store dismissal timestamp */
export function dismissPermissionBanner(): void {
  localStorage.setItem('browser-notif-dismissed', Date.now().toString());
}
